import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { takeWhile } from 'rxjs';
import {
  ICoupon,
  IProductSubscriptionPhase,
  ISubscription,
  RENEWAL_HISTORY_TYPES
} from '@care-portals/api-interfaces';
import { SubscriptionsFacade } from '../../../+store/subscriptions/subscriptions.facade';
import { CRMService } from '../../../services/api/crm.service';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzNotificationServiceModule } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NotesModule } from '../../../modules/notes.module';
import { UserDetailsPipe } from '../../../pipes/user-details.pipe';
import { formTypes, SUBSCRIPTION_STATUSES } from '@appgen-health-crm/data';
import { mapValues } from 'lodash';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { AppFacade } from '../../../+store/app/app.facade';
import { getAvailableRequirements, getPhaseIndexByCycle } from '@care-portals/shared';

@Component({
  selector: 'portals-emr-subscription-details',
  templateUrl: './subscription-details.component.html',
  styleUrls: [],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzLayoutModule,
    NzSelectModule,
    NzTableModule,
    NzButtonModule,
    NzSpinModule,
    NzTagModule,
    NzInputModule,
    NzFormModule,
    NzDividerModule,
    NzTimelineModule,
    NzToolTipModule,
    NzIconModule,
    NzDescriptionsModule,
    NzNotificationServiceModule,
    NotesModule,
    UserDetailsPipe,
    NzInputNumberModule
  ]
})
export class SubscriptionDetailsComponent implements OnInit, OnDestroy {
  @Input() subscriptionId: string;
  subscription: ISubscription | null = null;
  subscriptionStatuses = {};
  error: string | null = null;
  active = true;
  FG: FormGroup;
  requirements: Array<{ value: string; label: string; type: string }> = [];
  loading$ = this.subscriptionsFacade.loading$;
  activePhaseIndex: number | null = null;
  coupons: ICoupon[] = [];
  selectedCouponCode: string | null = null;
  private lastCouponProductId: string | null = null;

  constructor(
    private readonly subscriptionsFacade: SubscriptionsFacade,
    private fb: FormBuilder,
    private appFacade: AppFacade,
    private crmService: CRMService
  ) {
    SUBSCRIPTION_STATUSES.forEach(status => {
      this.subscriptionStatuses[status.name] = status.label;
    });
  }

  get hasZeroCyclePhases() {
    return this.subscriptionPhases.value.some(phase => phase.cycles === 0);
  }

  get subscriptionPhases() {
    return this.FG?.get('subscriptionPhases') as FormArray<
      AbstractControl<
        Partial<IProductSubscriptionPhase> & {
          requirements?: string[];
        }
      >
    >;
  }

  ngOnInit() {
    if (!this.subscriptionId) {
      this.error = 'Subscription ID is missing';
      return;
    }

    this.appFacade.selectedLayout$
      .pipe(takeWhile(() => this.active))
      .subscribe(org => {
        this.requirements = getAvailableRequirements(org).map(req => ({
          value: req.key,
          label: req.name,
          type: req.type
        }));
      });

    this.subscriptionsFacade.selectedSubscription$
      .pipe(takeWhile(() => this.active))
      .subscribe(subscription => {
        this.subscription = subscription;
        this.selectedCouponCode = subscription?.couponCode ?? subscription?.meta?.couponCode ?? '';
        this.activePhaseIndex =
          subscription && subscription.phases?.length
            ? getPhaseIndexByCycle(
              subscription.phases,
              (subscription.currentCycle || 0) + 1
            )
            : null;

        this.FG = this.fb.group({
          subscriptionPhases: this.fb.array(
            this.subscription?.phases?.map(phase =>
              this.createPhaseFormGroup(phase)
            ) || [],
            [this.validateSubscriptionPhases]
          )
        });

        const productId =
          typeof subscription?.product === 'string'
            ? subscription.product
            : subscription?.product?._id;

        if (productId && productId !== this.lastCouponProductId) {
          this.crmService
            .getCoupons({ affectedProducts: productId })
            .pipe(takeWhile(() => this.active))
            .subscribe(response => {
              this.coupons = response.results || [];
              this.lastCouponProductId = productId;
            });
        }
      });
    this.subscriptionsFacade.getSubscription(this.subscriptionId);
  }

  ngOnDestroy(): void {
    this.active = false;
    this.activePhaseIndex = null;
    this.subscriptionsFacade.clearSubscription();
    this.coupons = [];
    this.selectedCouponCode = null;
  }

  closeDrawer() {
    this.subscriptionsFacade.closeSubscriptionDetailsDrawer();
  }

  onCouponChange(couponCode: string | null) {
    this.subscriptionsFacade.updateSubscription(this.subscriptionId, {
      couponCode: couponCode || null
    });
  }

  validateSubscriptionPhases = (
    control: AbstractControl
  ): ValidationErrors | null => {
    if (!(control instanceof FormArray)) {
      return null;
    }

    const phases = control.value;
    const zeroCyclePhases = phases.filter(phase => phase.cycles === 0);

    if (zeroCyclePhases.length > 1) {
      return { multipleZeroCycles: 'Only one zero cycle phase is allowed' };
    }

    if (zeroCyclePhases.length === 1) {
      const zeroCycleIndex = phases.findIndex(phase => phase.cycles === 0);
      if (zeroCycleIndex !== phases.length - 1) {
        return { zeroCycleNotLast: 'Zero cycle phase must be the last phase' };
      }
    }

    return null;
  };

  createPhaseFormGroup(data: IProductSubscriptionPhase) {
    return this.fb.group(
      {
        price: [data.price, [Validators.required]],
        cycles: [data.cycles, [Validators.required, Validators.min(0)]],
        requirements: [(data.requirements || []).map(_ => _.key), []],
        fillingCycleInterval: [
          data.fillingCycleInterval,
          [Validators.required]
        ],
        fillingCycleUnit: [data.fillingCycleUnit, [Validators.required]],
        billableCycleInterval: [
          data.billableCycleInterval || 1,
          [Validators.required, Validators.min(1)]
        ],
        fulfillmentCycleInterval: [
          data.fulfillmentCycleInterval || 1,
          [Validators.required, Validators.min(1)]
        ],
        requirementCycleInterval: [
          data.requirementCycleInterval || 1,
          [Validators.required, Validators.min(1)]
        ]
      },
      { validators: [this.validateCycleIntervals] }
    );
  }

  addNewSubscriptionPhase() {
    if (this.hasZeroCyclePhases) {
      return;
    }

    const phases = this.createPhaseFormGroup({
      price: this.FG.get('price')?.value || this.FG.get('listPrice')?.value,
      cycles: 0,
      requirements: [],
      fillingCycleInterval: 1,
      fillingCycleUnit: 'month',
      billableCycleInterval: 1,
      fulfillmentCycleInterval: 1,
      requirementCycleInterval: 1
    });
    this.subscriptionPhases.push(phases as any);
  }

  validateCycleIntervals = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const cycles = control.get('cycles')?.value;
    const billableCycleInterval = control.get('billableCycleInterval')?.value;
    if (cycles > 0 && billableCycleInterval > cycles) {
      return {
        billableCycleInterval:
          'Cycles must be more than or equal to the billable cycle interval'
      };
    }
    const fulfillmentCycleInterval = control.get('fulfillmentCycleInterval')?.value;
    if (cycles > 0 && fulfillmentCycleInterval > cycles) {
      return {
        fulfillmentCycleInterval:
          'Cycles must be more than or equal to the fulfillment cycle interval'
      };
    }
    const requirementCycleInterval = control.get('requirementCycleInterval')?.value;
    if (cycles > 0 && requirementCycleInterval > cycles) {
      return {
        requirementCycleInterval:
          'Cycles must be more than or equal to the requirement cycle interval'
      };
    }
    return null;
  };

  submit() {
    if (this.FG.invalid) {
      this.touchAll(this.FG);
      return;
    }

    const { subscriptionPhases } = this.FG.value;

    const phases: IProductSubscriptionPhase[] = subscriptionPhases.map(
      phase => ({
        ...phase,
        fulfillmentCycleInterval: Number(phase.fulfillmentCycleInterval || 1),
        requirementCycleInterval: Number(phase.requirementCycleInterval || 1),
        requirements: phase.requirements.map(reqName => ({
          type: this.requirements.find(_ => reqName === _.value)?.type,
          key: reqName
        }))
      })
    );

    this.subscriptionsFacade.updateSubscription(this.subscriptionId, {
      phases
    });
  }

  touchAll(formGroup: FormGroup | FormArray): void {
    mapValues(formGroup.controls, (c, i) => {
      if (c instanceof FormGroup || c instanceof FormArray) {
        this.touchAll(c);
      } else {
        c['markAsDirty']();
        c['updateValueAndValidity']();
      }
    });
  }

  getActionLabel(renewal: any): string {
    const actionText =
      renewal.type === RENEWAL_HISTORY_TYPES.skip_cycle
        ? 'Cycle Skipped'
        : renewal.type === RENEWAL_HISTORY_TYPES.renewed
          ? 'Subscription Renewed'
          : renewal.type === RENEWAL_HISTORY_TYPES.update_renewal_date
            ? 'Renewal Date Changed'
            : 'Renewed At';

    const formattedDate = new Date(
      renewal.updatedAt ?? renewal.renewedAt
    ).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return `${actionText} - ${formattedDate}`;
  }
}
